// src/components/Admin/SellerManagement/ReviewModerationTab.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getModerationQueue, moderateReview, getSellerReviewsForAdmin } from '../../../../services/AdminApis';
import { Button } from "../../../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../../../components/ui/card";
import { Badge } from "../../../../components/ui/badge";
import { Textarea } from "../../../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "../../../../components/ui/alert";
import { CheckCircle, XCircle, Flag, Clock, User, Mail } from 'lucide-react';
import { useToast } from '../../../../hooks/use-toast';

const ReviewCard = ({ review, onModerate, isGlobalView = false }) => {
    const { toast } = useToast();
    const [notes, setNotes] = useState('');

    const getStatusBadgeVariant = (status) => {
        switch (status) {
            case 'PENDING': return 'secondary';
            case 'UNDER_DISPUTE': return 'destructive';
            case 'APPROVED': return 'success';
            case 'REJECTED': return 'destructive';
            default: return 'outline';
        }
    };

    return (
        <Card>
            <CardHeader className="flex flex-row justify-between items-start">
                <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                        <User className="w-4 h-4" /> {review.reviewer_name}
                    </CardTitle>
                    <CardDescription className="flex items-center gap-2 mt-1 text-xs">
                         <Mail className="w-3 h-3" /> {review.reviewer_email || 'N/A'}
                    </CardDescription>
                    {isGlobalView && (
                        <CardDescription className="mt-1 text-xs">
                            For Seller: <Link to={`/admin/sellers/${review.seller_id}/view/reviews`} className="underline font-semibold">{review.seller_id}</Link>
                        </CardDescription>
                    )}
                </div>
                <div className="text-right">
                    <p className="font-bold">Rating: {review.rating} / 5</p>
                    <Badge variant={getStatusBadgeVariant(review.status)} className="mt-2">
                        {review.status === 'PENDING' && <Clock className="w-3 h-3 mr-1" />}
                        {review.status === 'UNDER_DISPUTE' && <Flag className="w-3 h-3 mr-1" />}
                        {review.status === 'APPROVED' && <CheckCircle className="w-3 h-3 mr-1" />}
                        {review.status === 'REJECTED' && <XCircle className="w-3 h-3 mr-1" />}
                        {review.status}
                    </Badge>
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground p-4 bg-muted rounded-md">{review.description}</p>
                {review.moderation_notes && (
                     <div className="text-xs text-red-500 border-l-2 border-red-500 pl-2">
                        <strong>Previous Notes:</strong> {review.moderation_notes}
                    </div>
                )}
                <div>
                    <Textarea 
                        placeholder="Add moderation notes (required for rejection)..."
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                    />
                </div>
                <div className="flex justify-end gap-2">
                    <Button 
                        variant="outline" 
                        size="sm" 
                        className="text-red-600 hover:bg-red-50 hover:text-red-700"
                        onClick={() => {
                            if (!notes.trim()) {
                                toast({
                                    variant: "destructive",
                                    title: "Notes Required",
                                    description: "Moderation notes are required to reject a review.",
                                });
                                return;
                            }
                            onModerate(review.id, 'REJECTED', notes);
                        }}
                    >
                        <XCircle className="w-4 h-4 mr-2" />
                        Reject
                    </Button>
                    <Button 
                        variant="outline" 
                        size="sm"
                        className="text-green-600 hover:bg-green-50 hover:text-green-700"
                        onClick={() => onModerate(review.id, 'APPROVED', notes)}
                    >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Approve
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
};

const ReviewModerationTab = ({ sellerProfileId }) => {
    const { toast } = useToast();
    const [reviews, setReviews] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [statusFilter, setStatusFilter] = useState(''); // '' for all, used in global view

    useEffect(() => {
        const fetchReviews = async () => {
            setIsLoading(true);
            setError('');
            try {
                let res;
                if (sellerProfileId) {
                    // Fetch all reviews for a specific seller
                    res = await getSellerReviewsForAdmin(sellerProfileId);
                } else {
                    // Fetch the global moderation queue
                    res = await getModerationQueue(statusFilter);
                }
                setReviews(res.data);
            } catch (err) {
                const errorMessage = sellerProfileId ? 'Failed to fetch reviews for this seller.' : 'Failed to fetch reviews for moderation.';
                setError(errorMessage);
                toast({
                    variant: "destructive",
                    title: "Error",
                    description: errorMessage,
                });
            } finally {
                setIsLoading(false);
            }
        };
        fetchReviews();
    }, [statusFilter, sellerProfileId]);

    const handleModerate = async (reviewId, newStatus, notes) => {
        try {
            const res = await moderateReview(reviewId, newStatus, notes);
            const updatedReview = res.data;
            toast({
                title: "Success",
                description: `Review has been ${newStatus.toLowerCase()}.`,
                variant: "success",
            });

            if (sellerProfileId) {
                // In seller-specific view, update the review in place
                setReviews(reviews.map(r => r.id === reviewId ? updatedReview : r));
            } else {
                // In global queue, remove the moderated review
                setReviews(reviews.filter(r => r.id !== reviewId));
            }
        } catch (err) {
            toast({
                variant: "destructive",
                title: "Update Failed",
                description: "Failed to update review status.",
            });
        }
    };

    const isGlobalView = !sellerProfileId;

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold">
                    {isGlobalView ? 'Review Moderation Queue' : 'All Seller Reviews'}
                </h3>
                {isGlobalView && (
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">Filter by status:</span>
                        <Select onValueChange={setStatusFilter} defaultValue="">
                            <SelectTrigger className="w-[180px]">
                                <SelectValue placeholder="All Pending" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="All">All Pending</SelectItem>
                                <SelectItem value="PENDING">Pending</SelectItem>
                                <SelectItem value="UNDER_DISPUTE">Disputed</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                )}
            </div>

            {isLoading && <p>Loading reviews...</p>}
            {error && <Alert variant="destructive"><AlertTitle>Error</AlertTitle><AlertDescription>{error}</AlertDescription></Alert>}
            
            {!isLoading && reviews.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                    <CheckCircle className="mx-auto h-12 w-12" />
                    <p className="mt-4">{isGlobalView ? 'The moderation queue is empty. Great job!' : 'This seller has no reviews yet.'}</p>
                </div>
            )}

            <div className="space-y-4">
                {reviews.map(review => (
                    <ReviewCard key={review.id} review={review} onModerate={handleModerate} isGlobalView={isGlobalView} />
                ))}
            </div>
        </div>
    );
};

export default ReviewModerationTab;
